import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, BarChart3, Pencil, LinkIcon, Unlink, Camera, History } from "lucide-react";

export default function LinePlanning() {
  const [plans, setPlans] = useState([]);
  const [categories, setCategories] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ category_name: "", planned_styles: "", planned_units: "", price_min: "", price_max: "" });
  const [saving, setSaving] = useState(false);

  // Edit category state
  const [editCat, setEditCat] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Create line plan state
  const [collections, setCollections] = useState([]);
  const [showCreatePlan, setShowCreatePlan] = useState(false);
  const [planCollectionId, setPlanCollectionId] = useState("");

  // Versioning state
  const [showHistory, setShowHistory] = useState(false);

  // Style assignment state
  const [designs, setDesigns] = useState([]);
  const [assignCat, setAssignCat] = useState(null);
  const [assignDesignId, setAssignDesignId] = useState("");

  const loadPlans = () => {
    api.get("/line-plans")
      .then((r) => { setPlans(r.data); if (r.data.length > 0 && !selectedPlan) setSelectedPlan(r.data[0]); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    loadPlans();
    api.get("/collections").then((r) => setCollections(r.data)).catch(() => {});
    api.get("/design-studio/designs").then((r) => setDesigns(r.data)).catch(() => setDesigns([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedPlan) return;
    api.get(`/line-plans/${selectedPlan.id}/categories`).then((r) => setCategories(r.data)).catch(() => {});
    api.get(`/line-plans/${selectedPlan.id}/adjustments`).then((r) => setHistory(r.data)).catch(() => {});
  }, [selectedPlan]);

  const addCategory = async () => {
    setSaving(true);
    try {
      await api.post(`/line-plans/${selectedPlan.id}/categories`, {
        ...form,
        planned_styles: parseInt(form.planned_styles),
        planned_units: parseInt(form.planned_units),
        price_min: parseFloat(form.price_min),
        price_max: parseFloat(form.price_max),
      });
      toast.success("Category added");
      setShowAdd(false);
      setForm({ category_name: "", planned_styles: "", planned_units: "", price_min: "", price_max: "" });
      api.get(`/line-plans/${selectedPlan.id}/categories`).then((r) => setCategories(r.data));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const updateCategory = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (editForm.name !== undefined) payload.name = editForm.name;
      if (editForm.planned_style_count !== undefined) payload.planned_style_count = parseInt(editForm.planned_style_count) || 0;
      if (editForm.actual_style_count !== undefined) payload.actual_style_count = parseInt(editForm.actual_style_count) || 0;
      if (editForm.planned_units !== undefined) payload.planned_units = parseInt(editForm.planned_units) || 0;
      if (editForm.wholesale_price_min !== undefined) payload.wholesale_price_min = parseFloat(editForm.wholesale_price_min) || 0;
      if (editForm.wholesale_price_max !== undefined) payload.wholesale_price_max = parseFloat(editForm.wholesale_price_max) || 0;
      if (editForm.retail_price_min !== undefined) payload.retail_price_min = parseFloat(editForm.retail_price_min) || 0;
      if (editForm.retail_price_max !== undefined) payload.retail_price_max = parseFloat(editForm.retail_price_max) || 0;
      await api.patch(`/line-plan-categories/${editCat.id}`, payload);
      toast.success("Category updated");
      setEditCat(null);
      api.get(`/line-plans/${selectedPlan.id}/categories`).then((r) => setCategories(r.data));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const createSnapshot = async () => {
    if (!selectedPlan) return;
    try {
      await api.post(`/line-plans/${selectedPlan.id}/snapshot`);
      toast.success("Snapshot created");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const createLinePlan = async () => {
    if (!planCollectionId) return;
    const col = collections.find((c) => c.id === planCollectionId);
    if (col && col.status !== "published") {
      toast.warning("Collection must be published before creating a line plan.");
      return;
    }
    setSaving(true);
    try {
      // Send an explicit empty body — a bodyless POST is what triggered the 422.
      await api.post(`/collections/${planCollectionId}/line-plan`, {});
      toast.success("Line plan created");
      setShowCreatePlan(false);
      setPlanCollectionId("");
      loadPlans();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const assignStyle = async () => {
    if (!assignCat || !assignDesignId) return;
    try {
      await api.post(`/line-plan-categories/${assignCat.id}/assign-style`, { design_id: assignDesignId });
      toast.success("Design linked to category");
      setAssignCat(null);
      setAssignDesignId("");
      api.get(`/line-plans/${selectedPlan.id}/categories`).then((r) => setCategories(r.data));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const unassignStyle = async (catId, designId) => {
    try {
      await api.delete(`/line-plan-categories/${catId}/assign-style/${designId}`);
      toast.success("Design unlinked");
      api.get(`/line-plans/${selectedPlan.id}/categories`).then((r) => setCategories(r.data));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const startEdit = (c) => {
    setEditCat(c);
    setEditForm({
      name: c.name || c.category_name || "",
      planned_style_count: c.planned_style_count || c.planned_styles || 0,
      actual_style_count: c.actual_style_count || 0,
      planned_units: c.planned_units || 0,
      wholesale_price_min: c.wholesale_price_min || c.price_min || 0,
      wholesale_price_max: c.wholesale_price_max || c.price_max || 0,
      retail_price_min: c.retail_price_min || 0,
      retail_price_max: c.retail_price_max || 0,
    });
  };

  const totalPlanned = categories.reduce((s, c) => s + (c.planned_style_count || c.planned_styles || 0), 0);
  const totalActual = categories.reduce((s, c) => s + (c.actual_style_count || 0), 0);
  const totalUnits = categories.reduce((s, c) => s + (c.planned_units || 0), 0);
  const totalRevenue = categories.reduce((s, c) => {
    const units = c.planned_units || 0;
    const priceMin = c.wholesale_price_min || c.price_min || 0;
    const priceMax = c.wholesale_price_max || c.price_max || 0;
    return s + units * ((priceMin + priceMax) / 2);
  }, 0);
  const formatRevenue = (v) => "$" + Math.round(v).toLocaleString();

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">PLM</p>
          <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">Line Planning</h1>
          <p className="text-zinc-500 mt-2 text-sm">Category breakdown, MFP summaries and budget history.</p>
        </div>
        <button onClick={() => setShowCreatePlan(true)}
          className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">
          <Plus size={15} /> New line plan
        </button>
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-zinc-400">Loading...</div>
      ) : plans.length === 0 ? (
        <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
          <BarChart3 size={26} className="mx-auto mb-3" />
          <p className="text-sm">No line plans yet. Create one from a collection.</p>
        </div>
      ) : (
        <>
          <div className="mt-6 flex items-center gap-3">
            <select value={selectedPlan?.id || ""} onChange={(e) => setSelectedPlan(plans.find((p) => p.id === e.target.value))}
              className="border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
              {plans.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.season_code})</option>)}
            </select>
            <button onClick={createSnapshot}
              className="inline-flex items-center gap-1.5 border border-zinc-300 px-4 py-2 rounded-md text-sm font-medium hover:bg-zinc-50 transition-colors">
              <Camera size={14} /> Create Snapshot
            </button>
            <button onClick={() => setShowHistory(!showHistory)}
              className="inline-flex items-center gap-1.5 border border-zinc-300 px-4 py-2 rounded-md text-sm font-medium hover:bg-zinc-50 transition-colors">
              <History size={14} /> View History
            </button>
            <button onClick={() => setShowAdd(true)}
              className="inline-flex items-center gap-1.5 bg-zinc-950 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">
              <Plus size={14} /> Add category
            </button>
          </div>

          {/* MFP Summary Cards */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="border border-zinc-200 bg-white rounded-lg p-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Planned Styles</p>
              <p className="text-2xl font-bold mt-1">{totalPlanned}</p>
            </div>
            <div className="border border-zinc-200 bg-white rounded-lg p-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Actual Styles</p>
              <p className="text-2xl font-bold mt-1">{totalActual}</p>
              {totalPlanned > 0 && (
                <p className={`text-xs mt-1 ${totalActual >= totalPlanned ? "text-emerald-600" : "text-amber-600"}`}>
                  {Math.round((totalActual / totalPlanned) * 100)}% of plan
                </p>
              )}
            </div>
            <div className="border border-zinc-200 bg-white rounded-lg p-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Total Units</p>
              <p className="text-2xl font-bold mt-1">{totalUnits.toLocaleString()}</p>
            </div>
            <div className="border border-zinc-200 bg-white rounded-lg p-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Categories</p>
              <p className="text-2xl font-bold mt-1">{categories.length}</p>
            </div>
            <div className="border border-zinc-200 bg-white rounded-lg p-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Planned Revenue</p>
              <p className="text-2xl font-bold mt-1">{formatRevenue(totalRevenue)}</p>
            </div>
          </div>

          {/* Category Table */}
          <div className="mt-8 border border-zinc-200 bg-white rounded-lg overflow-hidden">
            <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 text-[11px] uppercase tracking-[0.15em] text-zinc-400">
              <span className="col-span-2">Category</span>
              <span className="col-span-2">Planned Styles</span>
              <span className="col-span-2">Actual Styles</span>
              <span className="col-span-2">Units</span>
              <span className="col-span-2">Price Range (Wholesale)</span>
              <span className="col-span-2 text-right">Actions</span>
            </div>
            {categories.length === 0 ? (
              <div className="px-6 py-8 text-center text-sm text-zinc-400">No categories added yet.</div>
            ) : (
              categories.map((c) => (
                <div key={c.id} className="grid grid-cols-12 items-center px-6 py-4 border-b border-zinc-50">
                  <span className="col-span-2 text-sm font-medium">{c.name || c.category_name}</span>
                  <span className="col-span-2 text-sm">{c.planned_style_count || c.planned_styles || 0}</span>
                  <span className="col-span-2 text-sm">
                    <span className={`font-medium ${(c.actual_style_count || 0) >= (c.planned_style_count || c.planned_styles || 0) ? "text-emerald-700" : "text-amber-700"}`}>
                      {c.actual_style_count || 0}
                    </span>
                  </span>
                  <span className="col-span-2 text-sm">{(c.planned_units || 0).toLocaleString()}</span>
                  <span className="col-span-2 text-sm">
                    {(c.wholesale_price_min || c.price_min || 0).toFixed(2)} - {(c.wholesale_price_max || c.price_max || 0).toFixed(2)}
                  </span>
                  <span className="col-span-2 text-right flex items-center justify-end gap-1">
                    <button onClick={() => { setAssignCat(c); setAssignDesignId(""); }} className="p-1.5 hover:bg-blue-50 rounded-md text-blue-500" title="Link design">
                      <LinkIcon size={14} />
                    </button>
                    <button onClick={() => startEdit(c)} className="p-1.5 hover:bg-zinc-100 rounded-md text-zinc-500 hover:text-zinc-900" title="Edit">
                      <Pencil size={14} />
                    </button>
                  </span>
                </div>
              ))
            )}
          </div>

          {/* Budget Adjustment History */}
          {showHistory && (
            <div className="mt-8">
              <h3 className="font-heading font-semibold text-sm mb-3">Budget Adjustment History</h3>
              {history.length === 0 ? (
                <div className="border border-zinc-200 bg-white rounded-lg p-8 text-center text-sm text-zinc-400">No adjustments recorded yet.</div>
              ) : (
                <div className="border border-zinc-200 bg-white rounded-lg overflow-hidden">
                  {history.map((h) => (
                    <div key={h.id} className="px-6 py-3 border-b border-zinc-50 text-sm flex items-center justify-between">
                      <span className="text-zinc-600">{h.reason || h.description}</span>
                      <div className="flex items-center gap-4">
                        {h.amount != null && <span className={`font-mono text-sm ${h.amount >= 0 ? "text-emerald-600" : "text-red-600"}`}>{h.amount >= 0 ? "+" : ""}{h.amount}</span>}
                        <span className="text-xs text-zinc-400">{h.created_at}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Add Category Modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowAdd(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">Add category</h2>
              <button onClick={() => setShowAdd(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input value={form.category_name} onChange={(e) => setForm({ ...form, category_name: e.target.value })} placeholder="Category name (e.g. Tops)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <div className="grid grid-cols-2 gap-3">
                <input type="number" value={form.planned_styles} onChange={(e) => setForm({ ...form, planned_styles: e.target.value })} placeholder="Planned styles"
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                <input type="number" value={form.planned_units} onChange={(e) => setForm({ ...form, planned_units: e.target.value })} placeholder="Planned units"
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input type="number" value={form.price_min} onChange={(e) => setForm({ ...form, price_min: e.target.value })} placeholder="Price min"
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                <input type="number" value={form.price_max} onChange={(e) => setForm({ ...form, price_max: e.target.value })} placeholder="Price max"
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              </div>
              <button onClick={addCategory} disabled={!form.category_name || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Adding..." : "Add category"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {editCat && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setEditCat(null)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">Edit category</h2>
              <button onClick={() => setEditCat(null)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} placeholder="Category name"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-zinc-500 mb-1 block">Planned styles</label>
                  <input type="number" value={editForm.planned_style_count} onChange={(e) => setEditForm({ ...editForm, planned_style_count: e.target.value })}
                    className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                </div>
                <div>
                  <label className="text-[10px] text-zinc-500 mb-1 block">Actual styles</label>
                  <input type="number" value={editForm.actual_style_count} onChange={(e) => setEditForm({ ...editForm, actual_style_count: e.target.value })}
                    className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                </div>
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 mb-1 block">Planned units</label>
                <input type="number" value={editForm.planned_units} onChange={(e) => setEditForm({ ...editForm, planned_units: e.target.value })}
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-zinc-500 mb-1 block">Wholesale min</label>
                  <input type="number" value={editForm.wholesale_price_min} onChange={(e) => setEditForm({ ...editForm, wholesale_price_min: e.target.value })}
                    className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                </div>
                <div>
                  <label className="text-[10px] text-zinc-500 mb-1 block">Wholesale max</label>
                  <input type="number" value={editForm.wholesale_price_max} onChange={(e) => setEditForm({ ...editForm, wholesale_price_max: e.target.value })}
                    className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-zinc-500 mb-1 block">Retail min</label>
                  <input type="number" value={editForm.retail_price_min} onChange={(e) => setEditForm({ ...editForm, retail_price_min: e.target.value })}
                    className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                </div>
                <div>
                  <label className="text-[10px] text-zinc-500 mb-1 block">Retail max</label>
                  <input type="number" value={editForm.retail_price_max} onChange={(e) => setEditForm({ ...editForm, retail_price_max: e.target.value })}
                    className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                </div>
              </div>
              <button onClick={updateCategory} disabled={saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Line Plan Modal */}
      {showCreatePlan && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowCreatePlan(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">New line plan</h2>
              <button onClick={() => setShowCreatePlan(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <select value={planCollectionId} onChange={(e) => setPlanCollectionId(e.target.value)}
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="">Select a collection</option>
                {collections.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <button onClick={createLinePlan} disabled={!planCollectionId || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Creating..." : "Create line plan"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Assign Style Modal */}
      {assignCat && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setAssignCat(null)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">Link design to {assignCat.name}</h2>
              <button onClick={() => setAssignCat(null)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            {(assignCat.assigned_style_ids || []).length > 0 && (
              <div className="mb-4">
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-2">Linked designs</p>
                <div className="space-y-1">
                  {(assignCat.assigned_style_ids || []).map((sid) => {
                    const d = designs.find((ds) => ds.id === sid);
                    return (
                      <div key={sid} className="flex items-center justify-between bg-zinc-50 px-3 py-2 rounded-md text-sm">
                        <span>{d ? d.name : sid}</span>
                        <button onClick={() => unassignStyle(assignCat.id, sid)} className="p-1 hover:bg-red-50 rounded text-red-400" title="Unlink">
                          <Unlink size={12} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="space-y-4">
              <select value={assignDesignId} onChange={(e) => setAssignDesignId(e.target.value)}
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="">Select a design</option>
                {designs.filter((d) => !(assignCat.assigned_style_ids || []).includes(d.id)).map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
              <button onClick={assignStyle} disabled={!assignDesignId}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                Link design
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
